import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { setupPushNotifications } from './services/NotificationService';
import useLocationTracking from './services/useLocationTracking';

// ── Kept EAGER (loaded immediately, no lazy()) ──
// These are the very first screens someone sees before we even know
// their role yet, so there's nothing to gain from lazy-loading them -
// it would just add a spinner to the app's first paint.
import Login from "./pages/Login";
import LandingPage from "./pages/LandingPage";
import LocationSetup from "./pages/LocationSetup";
import AdminCategoryIcons from "./pages/AdminCategoryIcons";
// ── Everything else: LAZY ──
// Split per role so a customer's phone never downloads restaurant/
// delivery/admin dashboard code, and vice versa. Each import() becomes
// its own separate JS chunk, only fetched the moment that route is hit.

// Customer & Order Routes
const Home = lazy(() => import("./pages/Home"));
const Menu = lazy(() => import("./pages/Menu"));
const Cart = lazy(() => import("./pages/Cart"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const AddAddress = lazy(() => import("./pages/AddAddress"));
const Addresses = lazy(() => import("./pages/Addresses"));
const Profile = lazy(() => import("./pages/Profile"));
const CategoryItems = lazy(() => import("./pages/CategoryItems"));
const FoodTypeItems = lazy(() => import("./pages/FoodTypeItems"));
const Favorites = lazy(() => import("./pages/favorites"));
const Notifications = lazy(() => import("./pages/Notifications"));
const MyComplaints = lazy(() => import("./pages/MyComplaints"));
const CreateComplaint = lazy(() => import("./pages/CreateComplaint"));
const Offers = lazy(() => import("./pages/Offers")); // 🟢 NEW: Added Dedicated Offers Page

// Restaurant Owner Routes
const RestaurantDashboard = lazy(() => import("./pages/RestaurantDashboard"));
const RestaurantSetup = lazy(() => import("./pages/RestaurantSetup"));
const RestaurantSettings = lazy(() => import("./pages/RestaurantSettings"));
const RestaurantAnalytics = lazy(() => import("./pages/RestaurantAnalytics"));
const RestaurantMenu = lazy(() => import("./pages/RestaurantMenu"));
const RestaurantCoupons = lazy(() => import("./pages/RestaurantCoupons"));
const RestaurantSettlements = lazy(() => import("./pages/RestaurantSettlements"));
const RestaurantMarketing = lazy(() => import("./pages/RestaurantMarketing"));
const RestaurantSettlementOrders = lazy(() => import("./pages/RestaurantSettlementOrders"));
const RestaurantPartner = lazy(() => import("./pages/RestaurantPartner"));
const RestaurantRegister = lazy(() => import("./pages/RestaurantRegister"));

// Delivery Partner Routes
const DeliveryDashboard = lazy(() => import("./pages/DeliveryDashboard"));
const DeliveryHistory = lazy(() => import("./pages/DeliveryHistory"));
const DeliveryAnalytics = lazy(() => import("./pages/DeliveryAnalytics"));
const DeliverySettlements = lazy(() => import("./pages/DeliverySettlements"));
const DeliverySettlementOrders = lazy(() => import("./pages/DeliverySettlementOrders"));
const DeliveryPartner = lazy(() => import("./pages/DeliveryPartner"));
const DeliveryRegister = lazy(() => import("./pages/DeliveryRegister"));
const DeliveryOrders = lazy(() => import("./pages/DeliveryOrders"));
const deliveryprofile = lazy(() => import("./pages/deliveryprofile"));

// Admin Routes
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminStats = lazy(() => import("./pages/AdminStats"));
const AdminRestaurantHistory = lazy(() => import("./pages/AdminRestaurantHistory"));
const AdminDeliveryHistory = lazy(() => import("./pages/AdminDeliveryHistory"));
const AdminCustomerHistory = lazy(() => import("./pages/AdminCustomerHistory"));
const AdminOrderDetails = lazy(() => import("./pages/AdminOrderDetails"));
const AdminSettlements = lazy(() => import("./pages/AdminSettlements"));
const AdminSettlementOrders = lazy(() => import("./pages/AdminSettlementOrders"));
const AdminRefunds = lazy(() => import("./pages/AdminRefunds"));
const AdminComplaints = lazy(() => import("./pages/AdminComplaints"));
const AdminApplications = lazy(() => import("./pages/AdminApplications"));
const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const AdminCoupons = lazy(() => import("./pages/AdminCoupons"));
const Adminholidays = lazy(() => import("./pages/Adminholidays"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));

// Public / Utility / Legal Routes
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const SettlementOrders = lazy(() => import("./pages/SettlementOrders"));
const MobileTerms = lazy(() => import("./pages/MobileTerms"));
const MobilePrivacy = lazy(() => import("./pages/MobilePrivacy"));
const MobileRefunds = lazy(() => import("./pages/MobileRefunds"));
const MobileRestaurantAgreement = lazy(() => import("./pages/MobileRestaurantAgreement"));
const MobileDeliveryAgreement = lazy(() => import("./pages/MobileDeliveryAgreement"));

// Testing (rarely visited - definitely shouldn't ship in the main bundle)
const TestMap = lazy(() => import("./pages/TestMap"));
const TestMapbox = lazy(() => import("./pages/TestMapbox"));
const TestMapMyIndia = lazy(() => import("./pages/TestMapMyIndia"));

// Small, unobtrusive fallback shown only for the brief moment a chunk
// is downloading - not a full loading screen, since the app itself is
// already interactive by this point.
function RouteFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <div style={{ fontSize: '14px', color: '#8a8d94' }}>Loading…</div>
    </div>
  );
}

  
function App() {
  useLocationTracking();
  // ⏳ Loading state to prevent the "flash" of the login screen
  const [isAppLoading, setIsAppLoading] = useState(true);
  useEffect(() => {
    const initializeMobileApp = async () => {
      // 📱 1. HARDWARE BACK BUTTON LOGIC
      CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        const path = window.location.pathname;
        if (path === '/' || path === '/home' || path === '/login') {
          CapacitorApp.exitApp();
        } else {
          window.history.back();
        }
      });

      // 🔐 2. SESSION FIX: Check LocalStorage for your Python token!
      const token = localStorage.getItem("token");
      const role = localStorage.getItem("role");
      const userId = localStorage.getItem("user_id");
      const currentPath = window.location.pathname;

      // If they are logged in, but the app opened to the Login/Landing page, move them!
      if (token && (currentPath === '/' || currentPath === '/login')) {
          console.log("📱 Session found! Redirecting automatically...");
          if (role === "admin") window.location.replace("/admin-dashboard");
          else if (role === "restaurant_owner") window.location.replace("/restaurant-dashboard");
          else if (role === "delivery_partner") window.location.replace("/delivery-dashboard");
          else window.location.replace("/home");
      }

      // 🔔 3. Re-link push notifications in the background
      if (userId) {
          try {
              setupPushNotifications(userId);
          } catch (error) {
              console.log("Push setup background error:", error);
          }
      }

      // Turn off the loading screen and render the app!
      setIsAppLoading(false);
    };

    initializeMobileApp();

    return () => {
      CapacitorApp.removeAllListeners();
    };
  }, []);

  // ⏳ THE LOADING SCREEN
  if (isAppLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff' }}>
        <h2>Loading MealBee...</h2> 
      </div>
    );
  }
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Entry Point */}
          {/* Entry Point */}
<Route
  path="/"
  element={
    Capacitor.isNativePlatform()
      ? (
          localStorage.getItem("token")
            ? <Navigate to="/home" replace />
            : <Navigate to="/login" replace />
        )
      : <LandingPage />
  }
/>
          <Route path="/login" element={<Login />} />
          <Route path="/location-setup" element={<LocationSetup />} />

          {/* User & Order Routes */}
          <Route path="/home" element={<Home />} />
          <Route path="/menu/:restaurantId" element={<Menu />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/my-orders" element={<MyOrders />} />
          <Route path="/order/:orderId" element={<OrderDetails />} />
          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/add-address" element={<AddAddress />} />
          <Route path="/addresses" element={<Addresses />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/offers" element={<Offers />} /> {/* 🟢 NEW: Route for dedicated offers page */}

          {/* Dashboards */}
          <Route path="/restaurant-dashboard" element={<RestaurantDashboard />} />
          <Route path="/admin-dashboard" element={<AdminDashboard />} />
          <Route path="/delivery-dashboard" element={<DeliveryDashboard />} />

          {/* Administrative & Analytics */}
          <Route path="/admin-stats" element={<AdminStats />} />
          <Route path="/admin/restaurant-history/:id" element={<AdminRestaurantHistory />} />
          <Route path="/admin/delivery-history/:id" element={<AdminDeliveryHistory />} />
          <Route path="/admin/customer-history/:id" element={<AdminCustomerHistory />} />
          <Route path="/admin/order-details/:id" element={<AdminOrderDetails />} />
          <Route path="/admin-settlements" element={<AdminSettlements />} />
          <Route path="/admin-refunds" element={<AdminRefunds />} />
          <Route path="/admin-complaints" element={<AdminComplaints />} />
          <Route path="/admin-applications" element={<AdminApplications />} />
          <Route path="/admin-marketing" element={<AdminMarketing />} />
          <Route path="/admin-coupons" element={<AdminCoupons />} />
          <Route path="/admin-holidays" element={<Adminholidays />} />
          <Route path="/admin-category-icons" element={<AdminCategoryIcons />} />
          <Route
    path="/admin-analytics"
    element={<AdminAnalytics />}
/>

          {/* Restaurant & Partner Routes */}
          <Route path="/restaurant-setup" element={<RestaurantSetup />} />
          <Route path="/restaurant-settings" element={<RestaurantSettings />} />
          <Route path="/restaurant-analytics" element={<RestaurantAnalytics />} />
          <Route path="/restaurant-menu" element={<RestaurantMenu />} />
          <Route path="/restaurant-coupons" element={<RestaurantCoupons />} />
          <Route path="/restaurant-settlements" element={<RestaurantSettlements />} />
          <Route path="/restaurant-marketing" element={<RestaurantMarketing />} />
          <Route path="/restaurant-settlement-orders/:batchId" element={<RestaurantSettlementOrders />} />

          {/* Delivery Partner Routes */}
          <Route path="/delivery-history" element={<DeliveryHistory />} />
          <Route path="/delivery-analytics" element={<DeliveryAnalytics />} />
          <Route path="/delivery-settlements" element={<DeliverySettlements />} />
          <Route path="/delivery-settlement-orders/:batchId" element={<DeliverySettlementOrders />} />

          {/* Public & Utility Routes */}
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/category/:categoryName" element={<CategoryItems />} />
          <Route path="/food-type/:foodType" element={<FoodTypeItems />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/restaurant-partner" element={<RestaurantPartner />} />
          <Route path="/delivery-partner" element={<DeliveryPartner />} />
          <Route path="/restaurant-register" element={<RestaurantRegister />} />
          <Route path="/delivery-register" element={<DeliveryRegister />} />
          <Route path="/my-complaints" element={<MyComplaints />} />
          <Route path="/complaint/:orderId" element={<CreateComplaint />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/settlement-orders/:batchId" element={<SettlementOrders />} />

          <Route path="/app/terms" element={<MobileTerms />} />
          <Route path="/app/privacy" element={<MobilePrivacy />} />
          <Route path="/app/refunds" element={<MobileRefunds />} />
          <Route path="/restaurant-agreement" element={<MobileRestaurantAgreement />} />
          <Route path="/delivery-agreement" element={<MobileDeliveryAgreement />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/delivery-orders" element={<DeliveryOrders />} />
          <Route path="/delivery-profile" element={<deliveryprofile />} />
          {/* Testing */}
          <Route path="/test-map" element={<TestMap />} />
          <Route path="/test-mapbox" element={<TestMapbox />} />
          <Route path="/test-mapmyindia" element={<TestMapMyIndia />} />
          {/* Catch-all Route: Redirects any broken links to the Landing Page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;